import { defineStore } from 'pinia';

import type {
  GenerateKybLinkData,
  GetKybStatusData,
  GetKybStatusResponseDtoOutput,
} from '@peanuds/shared';
import { peanudsApi, withAuthenticatedHeaders } from '@peanuds/shared';

import { computed } from 'vue';
import { useAuthSessionStore } from '../auth0/store/auth-session.store';
import { createVerificationSource } from './createVerificationSource';

export const useKybSourceStore = defineStore('kyb-source', () => {
  const authSessionStore = useAuthSessionStore();

  const getOrganizationId = () => {
    const organizationId = authSessionStore.organizationId;

    if (!organizationId) {
      throw new Error('Missing organization ID in session token');
    }

    return organizationId;
  };

  const {
    source: liveKyb,
    isLoading: isKybLoading,
    reset: resetKybState,
    fetch: fetchLiveKybStatus,
    ensureResolved: ensureLiveKybResolved,
  } = createVerificationSource<GetKybStatusResponseDtoOutput>({
    sourceLabel: 'KYB',
    getRequestKey: (userId) => {
      const organizationId = authSessionStore.organizationId;

      if (!userId || !organizationId) {
        return null;
      }

      return `${userId}:${organizationId}`;
    },
    load: async (_userId) => {
      const organizationId = getOrganizationId();
      const requestOptions = await withAuthenticatedHeaders<GetKybStatusData>({
        path: { organizationId },
      });

      const response = await peanudsApi.getKybStatus(requestOptions);

      return response.data ?? null;
    },
  });

  const kyb = computed(() => liveKyb.value);

  const fetchKybStatus = async (userId: string) => {
    await fetchLiveKybStatus(userId);
  };

  const ensureKybResolved = async (userId: string) => {
    await ensureLiveKybResolved(userId);
  };

  const generateKybVerificationLink = async () => {
    const organizationId = authSessionStore.organizationId;

    if (!organizationId) {
      console.error('Missing organization ID for KYB link generation');
      return null;
    }

    try {
      const response = await peanudsApi.generateKybLink(
        await withAuthenticatedHeaders<GenerateKybLinkData>({
          path: { organizationId },
        }),
      );

      return response.data?.url ?? null;
    } catch (error) {
      console.error('Failed to generate KYB link:', error);
      return null;
    }
  };

  return {
    kyb,
    isKybLoading,
    resetKybState,
    fetchKybStatus,
    ensureKybResolved,
    generateKybLink: generateKybVerificationLink,
  };
});
